import React from 'react';
import { Button, Card, Col, Container, Row, Stack } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isNull, isUndefined, startCase } from 'lodash';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faPencil, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { selectIsFrontline } from '@components/Auth';
import { Loading, CopyLinkButton, LoadingError } from '@components/Base';
import { OrderStatuses } from '@models/Order';
import { OrderInvoiceLink } from '@components/Feature/Order';
import { QuoteBaseInfoCardBody } from '@components/Feature/Quote';
import { HealthQuoteBenefitScheduleCardBody } from '@components/Feature/HealthQuote';

import { useCorporateHealthQuoteFetch } from '../hook';
import { routeCorporateHealthQuoteBuyEditPage, routeCorporateHealthQuoteBuyLockPage, routeCorporateHealthQuoteBuyPayByCashPage, } from '../route';
import { RightCol } from '../right-col';
import { UserCardBody } from './user-card-body';
import { ImportCardBody } from './import-card-body';

const CorporateHealthQuoteBuyReviewPage = () => {
  const { t } = useTranslation();
  const isFrontline = useSelector(selectIsFrontline);
  const corporateHealthQuote = useCorporateHealthQuoteFetch();

  if (isUndefined(corporateHealthQuote)) return <Loading />;
  if (isNull(corporateHealthQuote)) return <LoadingError />;

  const { status, orderNumber, orderEncryptedId } = corporateHealthQuote;
  const isPending = status === OrderStatuses.Pending;
  const isLocked = status === OrderStatuses.Locked;
  const isActive = status === OrderStatuses.Active;
  const shouldEdit = isFrontline && (isPending || isLocked || isActive);
  const shouldLock = isFrontline && isPending;
  const shouldPayByCash = isLocked && isFrontline;

  return (
    <Container className="my-3">
      <Row className="gy-5">
        <Col lg={8}>
          <Card>
            <Card.Header className="d-flex flex-row justify-content-between align-items-center">
              {t('common:summary')}
              <CopyLinkButton />
            </Card.Header>
            <QuoteBaseInfoCardBody {...corporateHealthQuote} />
            <HealthQuoteBenefitScheduleCardBody {...corporateHealthQuote} />
            {isFrontline && <ImportCardBody {...corporateHealthQuote} />}
            <UserCardBody {...corporateHealthQuote} />
          </Card>
        </Col>

        <RightCol corporateHealthQuote={corporateHealthQuote}>
          <Card.Body>
            {isFrontline && (
              <div className="text-end">
                <OrderInvoiceLink orderEncryptedId={orderEncryptedId} />
              </div>
            )}

            <Stack direction="vertical" gap={2}>
              {shouldEdit && (
                <Button as={Link} to={routeCorporateHealthQuoteBuyEditPage({ orderNumber })} variant="outline-primary">
                  <FontAwesomeIcon icon={faPencil} className="me-2" />
                  {t('common:edit')}
                </Button>
              )}

              {shouldLock && (
                <Button as={Link} to={routeCorporateHealthQuoteBuyLockPage({ orderNumber })} variant="warning">
                  <FontAwesomeIcon icon={faLock} className="me-2" />
                  {t('common:confirm')}
                </Button>
              )}

              {shouldPayByCash && (
                <Button as={Link} to={routeCorporateHealthQuoteBuyPayByCashPage({ orderNumber })} variant="primary">
                  <span className="me-2">{startCase(t('common:payByCash'))}</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              )}

            </Stack>
          </Card.Body>
        </RightCol>
      </Row>
    </Container>
  );
};

export { CorporateHealthQuoteBuyReviewPage };
